// eslint-disable-next-line jira/filename-case
import React, { Component, type ReactNode } from 'react';
import type { ApplicationEdition } from '@jira/platform__application-editions/src/edition.tsx';
import type { Application } from '@jira/platform__application-editions/src/application.tsx';
import sendExperienceAnalytics, {
	type AdditionalEventAttributes,
} from '@jira/platform__experience-tracking-analytics/src/index.tsx';
import {
	TASK_ABORT,
	type Action,
} from '@jira/platform__jira-experience-tracker/src/common/constants.tsx';
import Context, { type ExperienceContext } from '../common/context/index.tsx';
import type { TrackerCallbacks } from '../common/types.tsx';

type Props = {
	isExperienceReady?: boolean;
	experience: string;
	analyticsSource: string | null;
	application: Application | null;
	edition: ApplicationEdition | null;
	children: ReactNode;
	experienceId: string | null | undefined; // example: issueId (changes to this prop reset the experience tracking),
	parentTrackerCallbacks: TrackerCallbacks | null;
	additionalAttributes: AdditionalEventAttributes;
};
/**
 * Provides a framework for tracking user experiences across a React component tree.
 * It manages state related to the tracking of start, success, failure, and abort conditions
 * for user experiences, handling timers and lifecycle events to ensure accurate reporting.
 * Captures errors in child components to mark experiences as unsuccessful, facilitating
 * comprehensive tracking without manual error handling in every component.
 */
export default class ExperienceTrackingProvider extends Component<Props> {
	static defaultProps = {
		parentTrackerCallbacks: null,
		additionalAttributes: {},
	};

	constructor(props: Props) {
		super(props);
		this.resetExperience();
	}

	componentDidMount(): void {
		this.checkAndStartTimer();
	}

	componentDidUpdate(prevProps: Props): void {
		if (!prevProps.isExperienceReady && this.props.isExperienceReady) {
			this.checkAndStartTimer();
		}
	}

	componentDidCatch(error: Error): void {
		// This catches any errors that bubble up to the Provider that
		// may not be caught by any ExperienceTrackers
		this.trackExperience({
			wasExperienceSuccesful: false,
			location: 'error-reached-provider',
			additionalAttributes: {
				errorMessage: error.message,
				errorName: error.name,
				...this.props?.additionalAttributes,
			},
		});

		// We're deliberately rethrowing the error here because we don't want to interfere with
		// any error handling the container app may have in place.
		throw error;
	}

	componentWillUnmount(): void {
		clearTimeout(this.timerId);
	}

	onPending = (experienceTrackerId: string): void => {
		this.shouldTrackExperience = true;

		this.pendingExperienceTrackerIds.add(experienceTrackerId);

		this.checkAndStartTimer();
	};

	onClearPending = (experienceTrackerId: string): void => {
		this.pendingExperienceTrackerIds.delete(experienceTrackerId);
	};

	onSuccess = (experienceTrackerId: string): void => {
		this.pendingExperienceTrackerIds.delete(experienceTrackerId);

		this.checkAndStartTimer();
	};

	onFailure = (location: string, additionalAttributes?: AdditionalEventAttributes): void => {
		// We need to track failure *immediately*, otherwise this component may
		// be unmounted and we'd miss our chance.
		this.trackExperience({
			wasExperienceSuccesful: false,
			location,
			additionalAttributes,
		});
	};

	onAbort = (location: string, additionalAttributes?: AdditionalEventAttributes): void => {
		this.trackExperience({
			wasExperienceSuccesful: false,
			action: TASK_ABORT,
			location,
			additionalAttributes,
		});
	};

	checkAndStartTimer(): void {
		if (this.shouldTrackExperience && this.props.isExperienceReady) {
			this.startTimer();
		}
	}

	startTimer(): void {
		clearTimeout(this.timerId);

		this.timerId = setTimeout(() => {
			if (this.pendingExperienceTrackerIds.size === 0) {
				this.trackExperience({
					wasExperienceSuccesful: true,
				});
			}
		}, 1000);
	}

	timerId: NodeJS.Timeout | undefined;

	pendingExperienceTrackerIds: Set<string> = new Set<string>();

	// @ts-expect-error - TS2564 - Property 'shouldTrackExperience' has no initializer and is not definitely assigned in the constructor.
	shouldTrackExperience: boolean;

	// @ts-expect-error - TS2564 - Property 'hasTrackedExperience' has no initializer and is not definitely assigned in the constructor.
	hasTrackedExperience: boolean;

	// To avoid triggering updates in consumers on every render, keep a single copy that updates only on experience resets
	// @ts-expect-error - TS2564 - Property 'memoizedContext' has no initializer and is not definitely assigned in the constructor.
	memoizedContext: ExperienceContext;

	// Resetting must not call setState since this can be executed in the render phase
	resetExperience(): void {
		clearTimeout(this.timerId);
		this.timerId = undefined;
		this.shouldTrackExperience = false;
		this.hasTrackedExperience = false;
		this.pendingExperienceTrackerIds.clear();
		this.memoizedContext = {
			consumerCallbacks: {
				onPending: this.onPending,
				onClearPending: this.onClearPending,
				onSuccess: this.onSuccess,
				onFailure: this.onFailure,
				onAbort: this.onAbort,
			},
			experienceId: this.props.experienceId,
			experience: this.props.experience,
		};
	}

	trackExperience({
		wasExperienceSuccesful,
		action,
		location,
		additionalAttributes,
	}: {
		wasExperienceSuccesful: boolean;
		action?: Action;
		location?: string;
		additionalAttributes?: AdditionalEventAttributes;
	}): void {
		if (this.hasTrackedExperience) {
			// We only ever want to send a single success/failure event for an experience
			return;
		}
		this.hasTrackedExperience = true;
		const { parentTrackerCallbacks, additionalAttributes: additionalAttributesFromProps } =
			this.props;
		if (parentTrackerCallbacks) {
			if (wasExperienceSuccesful) {
				parentTrackerCallbacks.onExperienceSuccess();
			} else {
				parentTrackerCallbacks.onExperienceFailure(location, {
					...additionalAttributesFromProps,
					...additionalAttributes,
				});
			}
		} else {
			const { experience } = this.memoizedContext;
			const { analyticsSource, application, edition } = this.props;
			analyticsSource &&
				sendExperienceAnalytics({
					experience,
					action,
					wasExperienceSuccesful,
					location,
					analyticsSource,
					application,
					edition,
					additionalAttributes: {
						...additionalAttributesFromProps,
						...additionalAttributes,
					},
				});
		}
	}

	// Check every render to reset the experience state instance variables
	checkExperienceIdForExperienceReset(): void {
		if (this.memoizedContext.experienceId !== this.props.experienceId) {
			this.resetExperience();
		}
	}

	render(): React.JSX.Element {
		this.checkExperienceIdForExperienceReset();
		return <Context.Provider value={this.memoizedContext}>{this.props.children}</Context.Provider>;
	}
}
